import './ProjectToolbar.scss';
import { HvacArea } from '@belimo-retrofit-portal/logic';
import { useForm, useFormWatch } from '@form-ts/react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { NavTab } from 'src/modules/common/components/NavTab';
import { NavTabContainer } from 'src/modules/common/components/NavTabContainer';
import { Project } from 'src/modules/common/types/Project';
import { FormError } from 'src/modules/form/types/FormError';
import { getHvacFormData } from 'src/modules/hvac/selectors/getHvacFormData';
import { HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { getCurrentMatch } from 'src/modules/routing/selectors/getCurrentMatch';
import { RouteMatch } from 'src/modules/routing/types/RouteMatch';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

type Props = {
  readonly project: Project;
  readonly onShare: () => void;
};

export const ProjectToolbar = ({ project, onShare }: Props): React.ReactElement => {
  const routeState = useSelector(getCurrentMatch);

  const projectBaseUrl = `/project/${encodeURIComponent(project.id)}`;

  const formData = useParametricSelector(getHvacFormData, project);
  const hvacForm = useForm<HvacFormData, FormError>('hvac', {
    reinitialize: true,
    initialValues: formData,
  });

  const areasField = hvacForm.field.at('assessment').at('areas');
  const heatingEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.HEATING).at('enabled').value.get);
  const coolingEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.COOLING).at('enabled').value.get);
  const ventilationEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.VENTILATION).at('enabled').value.get);
  const hvacTabDisabled = !heatingEnabled && !coolingEnabled && !ventilationEnabled;

  return (
    <div className="bp-project-toolbar">
      <div className="bp-project-toolbar__content">

        <div className="bp-project-toolbar__controls">
          <NavTabContainer>
            <NavTab
              as={Link}
              to={projectBaseUrl}
              selected={getMatchedRoute(routeState, 'bp:project:basedata') !== null}
            >
              <FormattedMessage id="project/toolbar/navigation/basedata"/>
            </NavTab>

            <NavTab
              as={Link}
              to={`${projectBaseUrl}/hvac/settings`}
              selected={getMatchedRoute(routeState, 'bp:project:hvac:settings') !== null}
            >
              <FormattedMessage id="project/toolbar/navigation/settings"/>
            </NavTab>

            <NavTab
              as={Link}
              to={`${projectBaseUrl}/hvac/emission`}
              selected={getMatchedRoute(routeState, 'bp:project:hvac') !== null &&
                getMatchedRoute(routeState, 'bp:project:hvac:settings') === null}
              disabled={hvacTabDisabled}
            >
              <FormattedMessage id="project/toolbar/navigation/hvac"/>
            </NavTab>

            <NavTab
              as={Link}
              to={`${projectBaseUrl}/evaluation`}
              selected={getMatchedRoute(routeState, 'bp:project:evaluation') !== null}
            >
              <FormattedMessage id="project/toolbar/navigation/evaluation"/>
            </NavTab>

            <NavTab
              as={Link}
              to={`${projectBaseUrl}/report`}
              selected={getMatchedRoute(routeState, 'bp:project:report') !== null}
            >
              <FormattedMessage id="project/toolbar/navigation/report"/>
            </NavTab>
          </NavTabContainer>
        </div>
      </div>

      <ShareProject/>
    </div>
  );
};

function getMatchedRoute(match: RouteMatch | null, state: string): RouteMatch | null {
  if (match?.state.name.includes(state)) {
    return match;
  }

  return match?.parent
    ? getMatchedRoute(match.parent, state)
    : null;
}
